import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
    HiOutlineHome, 
    HiOutlineDocumentText, 
    HiOutlineCalendar, 
    HiOutlineSpeakerphone, 
    HiOutlinePhotograph, 
    HiOutlineTag, 
    HiOutlineUsers, 
    HiOutlineLogout,
    HiOutlineCog,
    HiOutlineMenu,
    HiOutlineX
} from 'react-icons/hi';
import './Layout.css';

const Layout = () => {
    const { user, logout, refreshUser } = useAuth();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(() => {
        return window.innerWidth < 1024;
    });
    const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);
    const [menuKey, setMenuKey] = useState(0); // Forzar re-render del menú

    const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:5001').replace(/\/api$/, '');

    // Refrescar permisos cada vez que se monta el layout
    useEffect(() => {
        refreshUser();
    }, []);

    // Re-render del menú cuando cambian los permisos del usuario
    useEffect(() => {
        setMenuKey(prev => prev + 1);
        console.log('📋 Menú actualizado. Usuario actual:', user?.email, 'Permisos:', user?.permissions);
    }, [user?.permissions]);

    useEffect(() => {
        const handleResize = () => {
            const mobile = window.innerWidth < 1024;
            setIsMobile(mobile);
            if (!mobile) {
                setIsCollapsed(false);
            } else {
                setIsCollapsed(true);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const hasPermission = (permission) => {
        if (!user) return false;
        
        // Verificar permisos específicos del usuario (todos los usuarios, incluyendo admins)
        const userPermissions = Array.isArray(user.permissions) ? user.permissions : [];
        const hasIt = userPermissions.includes(permission);
        
        console.log(`Verificando permiso "${permission}" para usuario ${user.email}: ${hasIt}. Permisos actuales:`, userPermissions);
        
        return hasIt;
    };

    const menuItems = [
        { path: '/dashboard', icon: <HiOutlineHome />, label: 'Dashboard', permission: null }, // Dashboard siempre visible
        { path: '/blog', icon: <HiOutlineDocumentText />, label: 'Blog', permission: 'blog' },
        { path: '/events', icon: <HiOutlineCalendar />, label: 'Eventos', permission: 'events' },
        { path: '/announcements', icon: <HiOutlineSpeakerphone />, label: 'Convocatorias', permission: 'announcements' },
        { path: '/banners', icon: <HiOutlinePhotograph />, label: 'Banners', permission: 'banners' },
        { path: '/categories', icon: <HiOutlineTag />, label: 'Categorías', permission: 'users' }, // Solo admin
        { path: '/users', icon: <HiOutlineUsers />, label: 'Usuarios', permission: 'users' }
    ];

    const filteredMenu = menuItems.filter(item => 
        item.permission === null || hasPermission(item.permission)
    );

    return (
        <div className="layout">
            {isMobile && (
                <button 
                    className="toggle-sidebar" 
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    title={isCollapsed ? 'Expandir menú' : 'Colapsar menú'}
                    style={{
                        left: isCollapsed ? '20px' : '280px'
                    }}
                >
                    {isCollapsed ? <HiOutlineMenu /> : <HiOutlineX />}
                </button>
            )}
            
            <aside className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
                <div className="sidebar-header">
                    {!isCollapsed && <h2>Fenine CMS</h2>}
                    {!isCollapsed && <span className="version">v1.0</span>}
                </div>
                
                <nav className="sidebar-nav">
                    {filteredMenu.map(item => (
                        <Link
                            key={item.path}
                            to={item.path}
                            className={`nav-item ${location.pathname === item.path ? 'active' : ''}`}
                            title={isCollapsed ? item.label : ''}
                        >
                            {item.icon}
                            {!isCollapsed && <span>{item.label}</span>}
                        </Link>
                    ))}
                </nav>

                <div className="sidebar-footer">
                    <Link to="/settings" className="user-info" title={isCollapsed ? user.full_name : ''}>
                        <div className="user-avatar">
                            {user.profile_image ? (
                                <img src={`${API_URL}${user.profile_image}`} alt={user.full_name} />
                            ) : (
                                user.full_name?.[0] || 'U'
                            )}
                        </div>
                        {!isCollapsed && (
                            <div className="user-details">
                                <div className="user-name">{user.full_name}</div>
                                <div className="user-role">{user.role}</div>
                            </div>
                        )}
                        {!isCollapsed && <HiOutlineCog className="settings-icon" />}
                    </Link>
                    <button onClick={logout} className="btn-logout" title={isCollapsed ? 'Cerrar Sesión' : ''}>
                        <HiOutlineLogout />
                        {!isCollapsed && <span>Cerrar Sesión</span>}
                    </button>
                </div>
            </aside>

            <main className="main-content">
                <Outlet />
            </main>
        </div>
    );
};

export default Layout;
